// src/pages/AdminPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard, getLowStock, triggerProcoreSync, getProcoreSyncStatus, getProcoreSyncConflicts, resolveSyncConflict, getToolRequests, updateToolRequest, getWorkers, updateWorker } from '../services/api';
import { AT_ROLE_OPTIONS } from '../components/DirectoryChangesModal';

export default function AdminPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loadError, setLoadError] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [conflicts, setConflicts] = useState([]);
  const [requests, setRequests] = useState([]);

  // Users tab: view every active worker and set their permission in each app.
  // Roles are set here by an admin and are never changed by the Procore sync.
  const [workers, setWorkers]             = useState([]);
  const [workerSearch, setWorkerSearch]   = useState('');
  const [workersLoaded, setWorkersLoaded] = useState(false);
  const [savingRole, setSavingRole]       = useState({}); // { [`${worker_id}:${field}`]: 'saving'|'done'|'error' }

  useEffect(() => {
    Promise.all([
      getDashboard().then(r => setStats(r.data)),
      getLowStock().then(r => setLowStock(r.data.items || [])),
      getProcoreSyncStatus().then(r => setSyncStatus(r.data.sync_history?.[0] || null)),
      getProcoreSyncConflicts().then(r => setConflicts(r.data.conflicts || [])),
      getToolRequests({ status: 'pending' }).then(r => setRequests(r.data.requests || [])),
    ]).catch(() => setLoadError(true));
  }, []);

  // Load the user list only when the tab is first opened.
  useEffect(() => {
    if (activeTab === 'users' && !workersLoaded) {
      getWorkers()
        .then(r => { setWorkers(r.data.workers || []); setWorkersLoaded(true); })
        .catch(() => {});
    }
  }, [activeTab, workersLoaded]);

  const handleRoleChange = async (workerId, field, value) => {
    const key = `${workerId}:${field}`;
    const flash = (state, ms) => {
      setSavingRole(s => ({ ...s, [key]: state }));
      setTimeout(() => setSavingRole(s => { const n = { ...s }; delete n[key]; return n; }), ms);
    };
    setSavingRole(s => ({ ...s, [key]: 'saving' }));
    try {
      await updateWorker(workerId, { [field]: value });
      setWorkers(prev => prev.map(w => w.worker_id === workerId ? { ...w, [field]: value } : w));
      flash('done', 1500);
    } catch {
      flash('error', 2500);
    }
  };

  const filteredWorkers = workers.filter(w => {
    const q = workerSearch.trim().toLowerCase();
    if (!q) return true;
    return [w.display_name, w.email, w.job_title, w.at_role, w.lb_role]
      .some(v => (v || '').toLowerCase().includes(q));
  });

  const handleSync = async () => {
    setSyncing(true);
    setSyncMessage('');
    try {
      await triggerProcoreSync();
      setSyncMessage('Sync started — this takes about 30 seconds. Refresh the page to see the result.');
      // Poll for completion after 35s
      setTimeout(async () => {
        try {
          const r = await getProcoreSyncStatus();
          setSyncStatus(r.data.sync_history?.[0] || null);
          setSyncMessage('');
        } catch {}
        setSyncing(false);
      }, 35000);
    } catch (err) {
      setSyncMessage(err.response?.data?.error || 'Sync failed to start.');
      setSyncing(false);
    }
  };

  const handleConflictResolve = async (conflictId, action) => {
    try {
      await resolveSyncConflict(conflictId, action);
      setConflicts(prev => prev.filter(c => c.conflict_id !== conflictId));
    } catch (err) {
      console.error('Failed to resolve conflict:', err);
    }
  };

  const handleRequestAction = async (requestId, status) => {
    try {
      await updateToolRequest(requestId, { status });
      setRequests(prev => prev.filter(r => r.request_id !== requestId));
    } catch (err) {
      console.error('Failed to update request:', err);
    }
  };

  return (
    <div className="screen theme-light">
      <div className="screen-header" style={{ justifyContent: 'space-between' }}>
        <h2>Admin</h2>
        <button
          className="btn btn-primary"
          style={{ width: 'auto', padding: '8px 16px', minHeight: 'auto' }}
          onClick={() => navigate('/admin/add')}
        >
          + Add Item
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', background: 'var(--procore-navy)', flexShrink: 0 }}>
        {['overview', 'users', 'low stock', 'requests'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={{
            flex: 1, padding: '12px 4px', background: 'none', border: 'none',
            color: activeTab === tab ? 'var(--procore-orange)' : 'rgba(255,255,255,0.6)',
            borderBottom: activeTab === tab ? '2px solid var(--procore-orange)' : '2px solid transparent',
            fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize'
          }}>
            {tab}
            {tab === 'low stock' && lowStock.length > 0 && (
              <span style={{ marginLeft: 4, background: 'var(--status-in-repair)', color: '#1A2C42', borderRadius: 10, padding: '1px 5px', fontSize: '0.65rem' }}>
                {lowStock.length}
              </span>
            )}
            {tab === 'requests' && requests.length > 0 && (
              <span style={{ marginLeft: 4, background: 'var(--procore-blue)', color: 'white', borderRadius: 10, padding: '1px 5px', fontSize: '0.65rem' }}>
                {requests.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="screen-content">
        {loadError && (
          <div className="alert alert-error" style={{ marginBottom: 12 }}>
            Could not load admin data. Check your connection.
          </div>
        )}

        {activeTab === 'overview' && stats && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Total Assets', value: stats.total_assets, icon: '🔧' },
                { label: 'Bulk Items', value: stats.total_bulk_items, icon: '📦' },
                { label: 'Checked Out', value: stats.checked_out, icon: '📤', color: 'var(--procore-orange)' },
                { label: 'Low Stock', value: lowStock.length, icon: '📦', color: 'var(--status-in-repair)' },
              ].map(s => (
                <div key={s.label} className="card" style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem' }}>{s.icon}</div>
                  <div style={{ fontSize: '2rem', fontWeight: 700, color: s.color || 'var(--light-text-primary)' }}>{s.value}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--light-text-muted)' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <button className="btn btn-navy" onClick={() => navigate('/search')}>
              Browse All Inventory →
            </button>

            {/* Procore Sync */}
            <div className="card" style={{ marginTop: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ fontSize: '0.95rem' }}>Sync Users with Procore</h3>
                <button
                  className="btn btn-primary"
                  style={{ width: 'auto', padding: '8px 16px', minHeight: 'auto', fontSize: '0.85rem' }}
                  onClick={handleSync}
                  disabled={syncing}
                >
                  {syncing ? 'Syncing…' : '↻ Procore Sync'}
                </button>
              </div>
              {syncMessage && (
                <p style={{ fontSize: '0.8rem', color: syncing ? 'var(--text-muted)' : 'var(--status-lost)', marginBottom: 8 }}>
                  {syncMessage}
                </p>
              )}
              {syncStatus ? (
                <div style={{ fontSize: '0.78rem', color: 'var(--light-text-muted)' }}>
                  <p>Last sync: {new Date(syncStatus.started_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                  <p>Status: <span style={{ color: syncStatus.status === 'completed' ? '#22c55e' : 'var(--status-lost)', fontWeight: 600, textTransform: 'capitalize' }}>{syncStatus.status}</span>
                    {syncStatus.workers_synced != null && ` · ${syncStatus.workers_synced} workers`}
                    {syncStatus.workers_deactivated > 0 && ` · ${syncStatus.workers_deactivated} deactivated`}
                  </p>
                </div>
              ) : (
                <p style={{ fontSize: '0.78rem', color: 'var(--light-text-muted)' }}>No sync history found.</p>
              )}

              {/* Pending conflicts */}
              {conflicts.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--light-text-primary)' }}>
                      Sync Conflicts
                    </span>
                    <span style={{ background: 'var(--procore-orange)', color: 'white', borderRadius: 10, padding: '1px 7px', fontSize: '0.7rem', fontWeight: 700 }}>
                      {conflicts.length}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: 'var(--light-text-muted)', marginBottom: 10 }}>
                    Procore values differ from the database. Choose to apply Procore's value or keep the current one.
                  </p>
                  {conflicts.map(c => (
                    <div key={c.conflict_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid var(--border-light)' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--light-text-primary)' }}>{c.display_name}</p>
                        <p style={{ fontSize: '0.75rem', color: 'var(--light-text-muted)', textTransform: 'capitalize' }}>{c.field}</p>
                        <p style={{ fontSize: '0.75rem' }}>
                          <span style={{ color: 'var(--light-text-muted)' }}>DB: </span>
                          <span style={{ color: 'var(--status-lost)', fontWeight: 600 }}>{c.db_value || '—'}</span>
                          <span style={{ color: 'var(--light-text-muted)', margin: '0 4px' }}>→</span>
                          <span style={{ color: '#22c55e', fontWeight: 600 }}>{c.procore_value || '—'}</span>
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button onClick={() => handleConflictResolve(c.conflict_id, 'apply')}
                          style={{ fontSize: '0.72rem', padding: '5px 8px', background: '#22c55e',
                            color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                          Use Procore
                        </button>
                        <button onClick={() => handleConflictResolve(c.conflict_id, 'keep')}
                          style={{ fontSize: '0.72rem', padding: '5px 8px', background: 'transparent',
                            color: 'var(--light-text-muted)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}>
                          Keep
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'low stock' && (
          lowStock.length === 0 ? (
            <div className="empty-state"><div style={{ fontSize: '2rem' }}>✅</div><p>All stock levels OK</p></div>
          ) : (
            <div className="card">
              {lowStock.map(item => (
                <div key={item.item_name} className="item-row">
                  <div className="item-row-icon">📦</div>
                  <div className="item-row-info">
                    <h4>{item.item_name}</h4>
                    <p>{item.available_quantity} {item.unit_of_measure} · {item.location_name}</p>
                  </div>
                  <span style={{
                    fontSize: '0.72rem', fontWeight: 600,
                    color: item.stock_status === 'out_of_stock' ? 'var(--status-lost)' : 'var(--status-in-repair)'
                  }}>
                    {item.stock_status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )
        )}

        {/* ── Users: permissions per app ── */}
        {activeTab === 'users' && (
          <div>
            <input
              className="input"
              placeholder="Search by name, email, title, or role..."
              value={workerSearch}
              onChange={e => setWorkerSearch(e.target.value)}
              style={{ marginBottom: 12 }}
              autoComplete="off"
            />
            {!workersLoaded ? (
              <div className="loading"><div className="spinner" /></div>
            ) : filteredWorkers.length === 0 ? (
              <div className="empty-state"><p>No users found</p></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {filteredWorkers.map(w => (
                  <div key={w.worker_id} className="card" style={{ padding: '12px 14px' }}>
                    <p style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--light-text-primary)', marginBottom: 1 }}>
                      {w.display_name || `${w.first_name} ${w.last_name}`}
                    </p>
                    {(w.job_title || w.email) && (
                      <p style={{ fontSize: '0.75rem', color: 'var(--light-text-muted)', marginBottom: 8 }}>
                        {[w.job_title, w.email].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {[['at_role', 'Asset Tracker'], ['lb_role', 'Labor Board']].map(([field, label]) => {
                      const state = savingRole[`${w.worker_id}:${field}`];
                      return (
                        <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--light-text-muted)', width: 96, flexShrink: 0 }}>
                            {label}
                          </label>
                          <select
                            className="input"
                            style={{ flex: 1, padding: '6px 10px', fontSize: '0.82rem' }}
                            value={w[field] || 'view_only'}
                            onChange={e => handleRoleChange(w.worker_id, field, e.target.value)}
                            disabled={state === 'saving'}
                          >
                            {AT_ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                          <span style={{ fontSize: '0.72rem', fontWeight: 700, width: 52, flexShrink: 0,
                            color: state === 'done' ? '#22c55e' : state === 'error' ? 'var(--status-lost)' : 'var(--text-muted)' }}>
                            {state === 'saving' ? 'Saving…' : state === 'done' ? '✓ Saved' : state === 'error' ? 'Error' : ''}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'requests' && (
          requests.length === 0 ? (
            <div className="empty-state"><div style={{ fontSize: '2rem' }}>✅</div><p>No pending tool requests</p></div>
          ) : (
            <div className="card">
              {requests.map(req => (
                <div key={req.request_id} className="item-row" style={{ alignItems: 'flex-start', gap: 10 }}>
                  <div className="item-row-icon" style={{ marginTop: 4 }}>📋</div>
                  <div className="item-row-info" style={{ flex: 1 }}>
                    <h4>{req.item_name}{req.asset_tag ? ` (${req.asset_tag})` : ''}</h4>
                    <p style={{ fontSize: '0.8rem' }}>By: {req.requested_by_name}</p>
                    {req.requested_for_location_name && (
                      <p style={{ fontSize: '0.8rem' }}>For: {req.requested_for_location_name}</p>
                    )}
                    {req.current_holder_name && (
                      <p style={{ fontSize: '0.72rem', color: 'var(--light-text-muted)' }}>
                        With: {req.current_holder_name}{req.current_location_name ? ` @ ${req.current_location_name}` : ''}
                      </p>
                    )}
                    {req.notes && (
                      <p style={{ fontSize: '0.72rem', color: 'var(--light-text-muted)', fontStyle: 'italic' }}>
                        "{req.notes}"
                      </p>
                    )}
                    <p style={{ fontSize: '0.68rem', color: 'var(--light-text-muted)', marginTop: 2 }}>
                      {new Date(req.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => handleRequestAction(req.request_id, 'fulfilled')}
                      style={{ fontSize: '0.72rem', padding: '5px 10px', background: '#22c55e',
                        color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>
                      ✓ Done
                    </button>
                    <button onClick={() => handleRequestAction(req.request_id, 'cancelled')}
                      style={{ fontSize: '0.72rem', padding: '5px 10px', background: 'transparent',
                        color: 'var(--light-text-muted)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }}>
                      Dismiss
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}